import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import SEO from '../../components/seo';
import Icon from '../../components/AppIcon';
import PropertyCard from '../property-search-browse/components/PropertyCard';
import { useGetAgentProfileQuery, useSubmitAgentReviewMutation } from '../../features/api/apiSlice';
import { selectIsAuthenticated } from '../../features/auth/authSlice';
import { useToast } from '../../components/ui/Toast';

const StarRow = ({ value = 0, size = 14, onSelect }) => (
  <div className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        type="button"
        disabled={!onSelect}
        onClick={() => onSelect?.(star)}
        className={onSelect ? 'cursor-pointer' : 'cursor-default'}
        aria-label={`${star} star${star === 1 ? '' : 's'}`}
      >
        <Icon
          name="Star"
          size={size}
          className={star <= Math.round(value) ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}
        />
      </button>
    ))}
  </div>
);

const AgentProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const addToast = useToast();
  const { data: agent, isLoading, isError } = useGetAgentProfileQuery(id, { skip: !id });
  const [submitReview, { isLoading: submitting }] = useSubmitAgentReviewMutation();
  const [form, setForm] = useState({ rating: 5, title: '', comment: '' });

  const initials = useMemo(
    () => (agent?.name || 'A').trim().charAt(0).toUpperCase(),
    [agent?.name]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isAuthenticated) {
      addToast('Log in to leave a review.', 'info');
      navigate('/login');
      return;
    }
    try {
      const res = await submitReview({
        id,
        rating: form.rating,
        title: form.title,
        comment: form.comment,
      }).unwrap();
      addToast(res.message || 'Review submitted for approval.', 'success');
      setForm({ rating: 5, title: '', comment: '' });
    } catch (error) {
      addToast(error?.data?.message || 'Could not submit the review.', 'error');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="pt-24 flex items-center justify-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-slate-200 border-t-slate-900" />
        </div>
      </div>
    );
  }

  if (isError || !agent) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="pt-24 text-center px-4">
          <h1 className="text-2xl font-semibold text-slate-900 mb-2">Profile not found</h1>
          <p className="text-slate-500 mb-6">This agent, broker, or advisor is not available.</p>
          <Link to="/property-search-browse" className="text-slate-900 underline">
            Back to listings
          </Link>
        </div>
      </div>
    );
  }

  const rating = agent.rating || { average: 0, count: 0, distribution: {} };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title={`${agent.name} | ${agent.role_label || 'Professional'} on HireBroker`}
        description={
          agent.professional_bio
            || `${agent.name} is a ${agent.role_label || 'real estate professional'} on HireBroker.`
        }
        canonicalUrl={`https://hirebroker.co/agent/${agent.id}`}
      />
      <Header />
      <main className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <section className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 sm:p-8 mb-6">
            <div className="flex flex-col md:flex-row md:items-start gap-6">
              <div className="w-20 h-20 rounded-2xl bg-slate-900 text-white flex items-center justify-center text-3xl font-semibold overflow-hidden flex-shrink-0">
                {agent.profile ? (
                  <img src={agent.profile} alt="" className="w-full h-full object-cover" />
                ) : (
                  initials
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="text-[11px] uppercase tracking-widest font-semibold text-slate-400">
                    {agent.role_label || 'Professional'}
                  </span>
                  {agent.is_verified && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-100">
                      <Icon name="BadgeCheck" size={12} />
                      HireBroker verified
                    </span>
                  )}
                  {agent.featured && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-800 border border-amber-100">
                      Featured
                    </span>
                  )}
                </div>
                <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">{agent.name}</h1>
                {agent.company_name && <p className="text-slate-500 mt-1">{agent.company_name}</p>}
                <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-slate-600">
                  <span className="inline-flex items-center gap-1.5">
                    <StarRow value={rating.average} />
                    <strong className="text-slate-900">{rating.average || '—'}</strong>
                    <span className="text-slate-400">({rating.count} review{rating.count === 1 ? '' : 's'})</span>
                  </span>
                  {agent.city && (
                    <span className="inline-flex items-center gap-1">
                      <Icon name="MapPin" size={14} className="text-slate-400" />
                      {agent.city}
                    </span>
                  )}
                  {agent.years_experience ? (
                    <span>{agent.years_experience}+ years experience</span>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8">
              {[
                { label: 'Listings', value: agent.listing_count ?? 0 },
                { label: 'Reviews', value: rating.count },
                { label: 'Rating', value: rating.average || '—' },
                { label: 'Experience', value: agent.years_experience ? `${agent.years_experience} yrs` : '—' },
              ].map((stat) => (
                <div key={stat.label} className="rounded-2xl bg-slate-50 border border-slate-100 px-4 py-3">
                  <p className="text-[11px] uppercase tracking-wide text-slate-400">{stat.label}</p>
                  <p className="text-xl font-semibold text-slate-900 mt-1">{stat.value}</p>
                </div>
              ))}
            </div>
          </section>

          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {agent.professional_bio && (
                <section className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                  <h2 className="text-lg font-semibold text-slate-900 mb-3">About</h2>
                  <p className="text-slate-600 leading-relaxed whitespace-pre-line">{agent.professional_bio}</p>
                </section>
              )}

              <section className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900 mb-1">Listings</h2>
                <p className="text-sm text-slate-500 mb-4">Properties currently listed by {agent.name}.</p>
                {agent.properties?.length ? (
                  <div className="grid sm:grid-cols-2 gap-4">
                    {agent.properties.map((property) => (
                      <PropertyCard key={property.id} property={property} />
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No public listings yet.</p>
                )}
              </section>

              <section className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Reviews</h2>
                {agent.reviews?.length ? (
                  <div className="space-y-4">
                    {agent.reviews.map((review) => (
                      <article key={review.id} className="border-b border-slate-100 pb-4 last:border-0 last:pb-0">
                        <div className="flex items-center justify-between gap-3">
                          <p className="font-medium text-slate-900">{review.reviewer_name}</p>
                          <StarRow value={review.rating} />
                        </div>
                        {review.title && <p className="text-sm font-medium text-slate-800 mt-1">{review.title}</p>}
                        <p className="text-sm text-slate-600 mt-1 leading-relaxed">{review.comment}</p>
                      </article>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No published reviews yet.</p>
                )}
              </section>
            </div>

            <aside className="space-y-6">
              <section className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900 mb-1">Verified documents</h2>
                <p className="text-xs text-slate-500 mb-4">
                  HireBroker checked these credentials. The files themselves are not shown on this page.
                </p>
                <ul className="space-y-2.5">
                  {(agent.verified_documents || []).map((doc) => (
                    <li key={doc.key} className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-slate-700">{doc.label}</span>
                      {doc.verified ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-medium">
                          <Icon name="CheckCircle2" size={14} />
                          Verified
                        </span>
                      ) : (
                        <span className="text-slate-400 text-xs">Not on file</span>
                      )}
                    </li>
                  ))}
                </ul>
                {agent.license_number && (
                  <p className="mt-4 text-xs text-slate-500">
                    Licence on file: <span className="text-slate-800 font-medium">{agent.license_number}</span>
                  </p>
                )}
                {agent.service_areas && (
                  <p className="mt-2 text-xs text-slate-500">Service areas: {agent.service_areas}</p>
                )}
              </section>

              <section className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900 mb-3">Write a review</h2>
                {agent.my_review?.status === 'approved' ? (
                  <p className="text-sm text-slate-500">You already reviewed this professional.</p>
                ) : agent.my_review?.status === 'pending' ? (
                  <p className="text-sm text-slate-500">Your review is waiting for HireBroker approval.</p>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-3">
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Rating</p>
                      <StarRow value={form.rating} size={20} onSelect={(rating) => setForm((prev) => ({ ...prev, rating }))} />
                    </div>
                    <input
                      type="text"
                      value={form.title}
                      onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
                      placeholder="Title (optional)"
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    />
                    <textarea
                      required
                      rows={4}
                      value={form.comment}
                      onChange={(event) => setForm((prev) => ({ ...prev, comment: event.target.value }))}
                      placeholder="How was your experience?"
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    />
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full h-11 rounded-xl bg-slate-900 text-white text-sm font-semibold disabled:opacity-60"
                    >
                      {isAuthenticated ? 'Submit for approval' : 'Log in to review'}
                    </button>
                  </form>
                )}
              </section>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AgentProfile;
